import React, { useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, ChevronDown, ChevronRight, Clock, ExternalLink, Inbox, Link2, Paperclip, Scale, Send, Trash2, X } from 'lucide-react';
import { describeError } from '../../services/errorMessage';
import { dokumentAnsehen } from '../dokumente/dokumentDatei';
import type { ProjektDokument } from '../dokumente/types';
import { useDokumente } from '../dokumente/useDokumente';
import {
    ANTWORT_VON_MAX,
    ANTWORT_WEG_LABEL,
    ANTWORT_WEGE,
    antwortVonAusEingabe,
    antwortVonGeaendert,
    antwortVonModus,
    antwortWegAus,
    auswahlFuerAntwortVon,
    type AntwortVonAuswahl,
} from './antwortQuelle';
import { ADRESSAT_LABEL, KAPITEL, KAPITEL_TITEL } from './fragenkatalog';
import {
    antwortMitDokument,
    dokumentVerweis,
    rueckflussVorschlaege,
    type AntwortStatusWahl,
    type AntwortVorschlag,
} from './rueckflussLogic';
import {
    ampelFuerFrage,
    filterFragen,
    gruppiereNachKapitel,
    istUeberfaellig,
    STATUS_LABEL,
    type FragenFilter,
} from './planungLogic';
import type { FrageStatus, KatalogKapitel, Planungsfrage } from './types';
import { adressatenIn, type EmpfaengerQuellen, type EmpfaengerZuordnung as Zuordnung } from './empfaenger';
import { EmpfaengerZuordnung } from './EmpfaengerZuordnung';
import { useEmpfaengerZuordnung } from './useEmpfaenger';

interface Props {
    fragen: Planungsfrage[];
    heuteIso: string;
    isReadOnly: boolean;
    /** Auswahl fuer Sammelaktionen (Versenden, Bedenkenanzeige, Export). */
    auswahl: Set<string>;
    onAuswahl: (ids: Set<string>) => void;
    onAendern: (id: string, updates: Partial<Planungsfrage>) => void;
    onLoeschen: (frage: Planungsfrage) => void;
    speichertId: string | null;
    /**
     * Quellen fuer die Vorschlaege „Empfaenger je Adressat" (Stufe 2.3):
     * Projektstammdaten (`report.metadata`: clientName/clientEmail) und der
     * Verteiler (`report.contacts`). Fehlen sie, gibt es keine Vorschlaege,
     * aber weiterhin die Eingabe von Hand.
     */
    empfaengerQuellen?: EmpfaengerQuellen | null;
    /**
     * Blendet den Abschnitt „Empfaenger je Adressat" oben in der Liste aus —
     * fuer den Leitfaden, der ihn in Station 5 zeigt. Die Zuordnung wird
     * trotzdem gelesen: die Zeilen zeigen den Empfaenger im Kopf.
     */
    ohneEmpfaenger?: boolean;
}

const AMPEL_FARBE: Record<string, string> = {
    grau: 'bg-slate-300',
    blau: 'bg-blue-500',
    gelb: 'bg-amber-400',
    gruen: 'bg-emerald-500',
    rot: 'bg-rose-500',
};

const KRIT_FARBE: Record<string, string> = {
    A: 'bg-rose-50 text-rose-700 border-rose-200',
    B: 'bg-amber-50 text-amber-700 border-amber-200',
    C: 'bg-slate-100 text-slate-500 border-slate-200',
};

const STATUS_WERTE: FrageStatus[] = [
    'offen', 'gestellt', 'teilbeantwortet', 'beantwortet', 'nicht_beantwortet', 'gegenstandslos',
];

/** Werte des Auswahlfelds „Antwort von". Eintraege tragen einen Vorsatz,
 *  damit kein Name mit „nicht erfasst" oder „Freie Angabe" zusammenfaellt. */
const WAHL_LEER = '';
const WAHL_FREI = 'frei';
const WAHL_EINTRAG = 'n:';
/** Zugeklappte Frage: keine Auswahl berechnen. */
const KEINE_AUSWAHL: AntwortVonAuswahl = { passend: [], weitere: [] };

/**
 * Feld „Antwort von": ein Auswahlfeld mit den Beteiligten des Projekts
 * (`auswahlFuerAntwortVon` — passend zur Frage, darunter alle weiteren aus
 * Verteiler und Stammdaten), „Freie Angabe …" und „— nicht erfasst —".
 *
 *   - Ein Eintrag oder „nicht erfasst" wird SOFORT gespeichert, nur bei
 *     geaendertem Wert (`antwortVonGeaendert`).
 *   - „Freie Angabe …" zeigt das Textfeld (mit Fokus); es hat einen eigenen
 *     Eingabestand und speichert erst beim Verlassen oder mit Enter, nur bei
 *     geaenderter Eingabe (getrimmt, leer = `null`) — nicht je Tastendruck.
 *
 * Was gewaehlt ist, leitet `antwortVonModus` aus dem Wert der Frage ab: ein
 * Wert aus der Liste waehlt seinen Eintrag, jeder andere zeigt die freie
 * Angabe mit diesem Wert. Entwurf und „Freie Angabe gewaehlt" gehoeren zur
 * Frage-Id und gelten nur, solange getippt bzw. frei angegeben wird — ein
 * von aussen neu geladener Wert erscheint sofort. Abgeleitet, kein Effekt.
 */
const AntwortVonFeld: React.FC<{
    frageId: string;
    wert: string | null | undefined;
    auswahl: AntwortVonAuswahl;
    isReadOnly: boolean;
    onSpeichern: (antwortVon: string | null) => void;
}> = ({ frageId, wert, auswahl, isReadOnly, onSpeichern }) => {
    const [entwurf, setEntwurf] = useState<{ frageId: string; text: string } | null>(null);
    /** „Freie Angabe …" gewaehlt (Frage-Id): das Textfeld bleibt, auch wenn der Wert leer ist oder in der Liste steht. */
    const [freiGewaehlt, setFreiGewaehlt] = useState<string | null>(null);
    /** Nach „Freie Angabe …" einmal ins Textfeld — gesetzt im Ereignis, verbraucht beim Einhaengen des Feldes. */
    const fokusGewuenscht = useRef(false);
    const offen = entwurf && entwurf.frageId === frageId ? entwurf : null;

    const modus = antwortVonModus(wert, [...auswahl.passend, ...auswahl.weitere]);
    const frei = freiGewaehlt === frageId || modus.modus === 'frei';
    const gewaehlt = frei ? WAHL_FREI : modus.modus === 'liste' ? `${WAHL_EINTRAG}${modus.eintrag}` : WAHL_LEER;

    const uebernimm = () => {
        if (!offen) return;
        setEntwurf(null);
        if (!isReadOnly && antwortVonGeaendert(offen.text, wert)) onSpeichern(antwortVonAusEingabe(offen.text));
    };

    const waehle = (wahl: string) => {
        if (isReadOnly) return;
        if (wahl === WAHL_FREI) {
            fokusGewuenscht.current = true;
            setFreiGewaehlt(frageId);
            return;
        }
        setFreiGewaehlt(null);
        setEntwurf(null);
        const neu = wahl.startsWith(WAHL_EINTRAG) ? antwortVonAusEingabe(wahl.slice(WAHL_EINTRAG.length)) : null;
        if (antwortVonGeaendert(neu, wert)) onSpeichern(neu);
    };

    return (
        <>
            <select
                value={gewaehlt}
                disabled={isReadOnly}
                onChange={e => waehle(e.target.value)}
                className="w-full px-3 py-1.5 border border-slate-200 rounded-lg text-xs disabled:bg-slate-50"
            >
                <option value={WAHL_LEER}>— nicht erfasst —</option>
                {auswahl.passend.length > 0 && (
                    <optgroup label="Passend zur Frage">
                        {auswahl.passend.map(n => <option key={n} value={`${WAHL_EINTRAG}${n}`}>{n}</option>)}
                    </optgroup>
                )}
                {auswahl.weitere.length > 0 && (
                    <optgroup label="Weitere Beteiligte">
                        {auswahl.weitere.map(n => <option key={n} value={`${WAHL_EINTRAG}${n}`}>{n}</option>)}
                    </optgroup>
                )}
                <option value={WAHL_FREI}>Freie Angabe …</option>
            </select>
            {frei && (
                <input
                    ref={el => {
                        if (!el || !fokusGewuenscht.current) return;
                        fokusGewuenscht.current = false;
                        el.focus();
                    }}
                    type="text"
                    maxLength={ANTWORT_VON_MAX}
                    value={offen ? offen.text : (wert || '')}
                    disabled={isReadOnly}
                    onChange={e => setEntwurf({ frageId, text: e.target.value })}
                    onBlur={uebernimm}
                    onKeyDown={e => {
                        if (e.key !== 'Enter' || e.nativeEvent.isComposing) return;
                        e.preventDefault();
                        uebernimm();
                    }}
                    className="w-full mt-1.5 px-3 py-1.5 border border-slate-200 rounded-lg text-xs disabled:bg-slate-50"
                    placeholder="Person und/oder Firma"
                    aria-label="Antwort von — freie Angabe"
                />
            )}
        </>
    );
};

const FrageZeile: React.FC<{
    frage: Planungsfrage;
    heuteIso: string;
    isReadOnly: boolean;
    gewaehlt: boolean;
    /** Zuordnung Adressat → Person der Planungsphase — Anzeige im Kopf und
     *  Vorschlaege fuer „Antwort von". */
    empfaenger: Zuordnung;
    /** Stammdaten und Verteiler — Vorschlaege und alle weiteren Beteiligten fuer „Antwort von". */
    empfaengerQuellen?: EmpfaengerQuellen | null;
    /** Der Beleg der Antwort aus der Ablage (`antwort_beleg_id`), soweit sichtbar. */
    beleg: ProjektDokument | null;
    /** Die Ablage ist gelesen — erst dann heisst „kein Beleg gefunden" auch „nicht verfügbar". */
    ablageGelesen: boolean;
    onWahl: (an: boolean) => void;
    onAendern: (updates: Partial<Planungsfrage>) => void;
    onLoeschen: () => void;
    speichert: boolean;
}> = ({
    frage, heuteIso, isReadOnly, gewaehlt, empfaenger, empfaengerQuellen, beleg, ablageGelesen,
    onWahl, onAendern, onLoeschen, speichert,
}) => {
    const [offen, setOffen] = useState(false);
    /** Fehler beim Oeffnen des Belegs — steht an der Zeile, nie still. */
    const [belegFehler, setBelegFehler] = useState<string | null>(null);
    const ampel = ampelFuerFrage(frage, heuteIso);
    const ueberfaellig = istUeberfaellig(frage, heuteIso);
    const gestelltWaehlbar = !!frage.gestellt_am || frage.status === 'gestellt';
    // Nur fuer die aufgeklappte Frage — die Liste mit 200 Fragen bleibt schnell.
    const antwortVonAuswahl = offen
        ? auswahlFuerAntwortVon(frage, { zuordnung: empfaenger, quellen: empfaengerQuellen })
        : KEINE_AUSWAHL;

    const belegOeffnen = () => {
        if (!beleg) return;
        setBelegFehler(null);
        dokumentAnsehen(beleg).catch(err => setBelegFehler(`Der Beleg konnte nicht geöffnet werden: ${describeError(err)}`));
    };

    return (
        <div className={`border-b border-slate-100 last:border-0 ${ueberfaellig ? 'bg-rose-50/40' : ''}`}>
            <div className="flex items-start gap-3 px-4 py-3">
                <input
                    type="checkbox"
                    checked={gewaehlt}
                    onChange={e => onWahl(e.target.checked)}
                    className="mt-1 shrink-0 w-4 h-4 rounded border-slate-300"
                    aria-label={`${frage.code} auswählen`}
                />
                <span className={`mt-1.5 shrink-0 w-2 h-2 rounded-full ${AMPEL_FARBE[ampel] || 'bg-slate-300'}`} />

                <button
                    type="button"
                    onClick={() => setOffen(o => !o)}
                    className="flex-1 text-left min-w-0"
                >
                    <div className="flex items-center gap-2 flex-wrap mb-0.5">
                        <span className="font-mono text-[11px] font-bold text-slate-500">{frage.code}</span>
                        <span className={`text-[10px] font-black px-1.5 py-0.5 rounded border ${KRIT_FARBE[frage.kritikalitaet]}`}>
                            {frage.kritikalitaet}
                        </span>
                        {/* Der zugeordnete Empfaenger steht NUR hier zur Anzeige —
                            in die Frage (`empfaenger_name`/`empfaenger_email`)
                            schreibt ihn erst der Versand (Stufe 4). */}
                        {(frage.adressat || []).map(a => {
                            const person = empfaenger[a];
                            const titel = person
                                ? `${ADRESSAT_LABEL[a]} — ${person.name}${person.email ? ` <${person.email}>` : ''}`
                                : ADRESSAT_LABEL[a];
                            return (
                                <span key={a} title={titel} className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200 max-w-full">
                                    {a}
                                    {person && (
                                        <span className="font-normal text-slate-500 truncate max-w-[12rem]">{person.name}</span>
                                    )}
                                </span>
                            );
                        })}
                        {ueberfaellig && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-black text-rose-600">
                                <Clock className="w-3 h-3" /> überfällig seit {String(frage.frist_am).slice(0, 10)}
                            </span>
                        )}
                        {frage.eigene_frage && (
                            <span className="text-[10px] text-indigo-500 font-bold">eigene Frage</span>
                        )}
                    </div>
                    <div className="text-sm text-slate-800 leading-snug pr-6">{frage.frage}</div>
                    {!offen && frage.antwort && (
                        <div className="text-xs text-slate-500 mt-1 line-clamp-2">{frage.antwort}</div>
                    )}
                </button>

                <div className="shrink-0 flex items-center gap-2">
                    <span className="text-[11px] text-slate-400 hidden sm:inline">{STATUS_LABEL[frage.status]}</span>
                    {offen ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                </div>
            </div>

            {offen && (
                <div className="px-4 pb-4 pl-14 space-y-3">
                    {(frage.rechtsgrundlage || []).length > 0 && (
                        <div className="flex items-start gap-2 text-[11px] text-slate-500">
                            <Scale className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                            <span>{(frage.rechtsgrundlage || []).join(' · ')}</span>
                        </div>
                    )}

                    <div>
                        <label className="block text-[11px] font-black text-slate-400 uppercase tracking-wider mb-1">
                            Antwort — wortgetreu, wie sie eingegangen ist
                        </label>
                        <textarea
                            value={frage.antwort}
                            disabled={isReadOnly}
                            onChange={e => onAendern({ antwort: e.target.value })}
                            rows={3}
                            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm disabled:bg-slate-50"
                            placeholder="Noch keine Antwort"
                        />
                        {/* Quelle der Antwort (Nr. 190) — ueber `onAendern`:
                            „Antwort von" aus der Auswahl sofort, als freie
                            Angabe beim Verlassen des Feldes bzw. mit Enter
                            (`AntwortVonFeld`), der Weg sofort. */}
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-2">
                            <label className="block sm:col-span-2">
                                <span className="block text-[11px] font-black text-slate-400 uppercase tracking-wider mb-1">Antwort von</span>
                                <AntwortVonFeld
                                    frageId={frage.id}
                                    wert={frage.antwort_von}
                                    auswahl={antwortVonAuswahl}
                                    isReadOnly={isReadOnly}
                                    onSpeichern={antwort_von => onAendern({ antwort_von })}
                                />
                            </label>
                            <label className="block">
                                <span className="block text-[11px] font-black text-slate-400 uppercase tracking-wider mb-1">Weg</span>
                                <select
                                    value={frage.antwort_weg || ''}
                                    disabled={isReadOnly}
                                    onChange={e => onAendern({ antwort_weg: antwortWegAus(e.target.value) })}
                                    className="w-full px-3 py-1.5 border border-slate-200 rounded-lg text-xs disabled:bg-slate-50"
                                >
                                    <option value="">nicht angegeben</option>
                                    {ANTWORT_WEGE.map(w => <option key={w} value={w}>{ANTWORT_WEG_LABEL[w]}</option>)}
                                </select>
                            </label>
                        </div>
                        {frage.antwort_beleg_id && (
                            <div className="flex items-center gap-1.5 flex-wrap mt-2 text-xs text-slate-500">
                                <Paperclip className="w-3.5 h-3.5 shrink-0" />
                                {beleg ? (
                                    <button
                                        type="button"
                                        onClick={belegOeffnen}
                                        className="inline-flex items-center gap-1 font-bold text-slate-600 hover:text-slate-800 max-w-full"
                                        title="Beleg ansehen"
                                    >
                                        <span className="truncate">Beleg: {beleg.titel || beleg.dateiname}</span>
                                        <ExternalLink className="w-3 h-3 shrink-0" />
                                    </button>
                                ) : (
                                    <span>{ablageGelesen ? 'Beleg: in der Ablage nicht verfügbar' : 'Beleg in der Ablage'}</span>
                                )}
                                {belegFehler && <span className="text-rose-600">{belegFehler}</span>}
                            </div>
                        )}
                    </div>

                    <div>
                        <label className="block text-[11px] font-black text-slate-400 uppercase tracking-wider mb-1">
                            Interne Bewertung — erscheint nicht im Blatt an den Bauherrn
                        </label>
                        <textarea
                            value={frage.bewertung}
                            disabled={isReadOnly}
                            onChange={e => onAendern({ bewertung: e.target.value })}
                            rows={2}
                            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-amber-50/40 disabled:bg-slate-50"
                            placeholder="Reicht die Antwort? Was folgt daraus?"
                        />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <label className="block">
                            <span className="block text-[11px] font-black text-slate-400 uppercase tracking-wider mb-1">Status</span>
                            {/* „Gestellt" gibt es nur mit Stelldatum und Frist — beides
                                setzt „Als gestellt markieren". Von Hand gewaehlt
                                stuende ein „gestellt" ohne Datum in der Akte, das
                                keine Ueberfaelligkeit ausloest und im Haftungsfall
                                nichts belegt. Zeigt die Zeile den Status schon
                                (Altbestand), bleibt er waehlbar, damit das
                                Auswahlfeld den IST-Stand nicht verschweigt. */}
                            <select
                                value={frage.status}
                                disabled={isReadOnly}
                                onChange={e => {
                                    const status = e.target.value as FrageStatus;
                                    if (status === 'gestellt' && !gestelltWaehlbar) return;
                                    onAendern({ status });
                                }}
                                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm disabled:bg-slate-50"
                            >
                                {STATUS_WERTE.map(s => (
                                    <option key={s} value={s} disabled={s === 'gestellt' && !gestelltWaehlbar}>
                                        {STATUS_LABEL[s]}
                                    </option>
                                ))}
                            </select>
                            {!gestelltWaehlbar && !isReadOnly && (
                                <span className="block text-[11px] text-slate-400 mt-1 leading-snug">
                                    „Gestellt" nur über „Als gestellt markieren" — das setzt Stelldatum und Frist.
                                </span>
                            )}
                        </label>
                        <label className="block">
                            <span className="block text-[11px] font-black text-slate-400 uppercase tracking-wider mb-1">Frist</span>
                            <input
                                type="date"
                                value={String(frage.frist_am || '').slice(0, 10)}
                                disabled={isReadOnly}
                                onChange={e => onAendern({ frist_am: e.target.value || null })}
                                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm disabled:bg-slate-50"
                            />
                        </label>
                        <label className="block">
                            <span className="block text-[11px] font-black text-slate-400 uppercase tracking-wider mb-1">Empfänger</span>
                            <input
                                type="text"
                                value={frage.empfaenger_name || ''}
                                disabled={isReadOnly}
                                onChange={e => onAendern({ empfaenger_name: e.target.value })}
                                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm disabled:bg-slate-50"
                                placeholder="Name des Adressaten"
                            />
                        </label>
                    </div>

                    <div className="flex items-center justify-between gap-3 pt-1">
                        <div className="text-[11px] text-slate-400">
                            {frage.gestellt_am
                                ? <>Gestellt am {String(frage.gestellt_am).slice(0, 10)}</>
                                : <>Noch nicht gestellt</>}
                            {frage.beantwortet_am && <> · beantwortet am {String(frage.beantwortet_am).slice(0, 10)}</>}
                            {speichert && <span className="ml-2 text-blue-500">speichert …</span>}
                        </div>
                        {frage.eigene_frage && !isReadOnly && (
                            <button
                                type="button"
                                onClick={onLoeschen}
                                className="inline-flex items-center gap-1 text-[11px] font-bold text-rose-600 hover:text-rose-700"
                            >
                                <Trash2 className="w-3.5 h-3.5" /> Frage löschen
                            </button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

// ─── Ruecklauf: Dokument als Antwort verknuepfen (Plan 4.3) ─────────────────

const STATUS_WAHL: { wert: AntwortStatusWahl; label: string; erklaerung: string }[] = [
    { wert: 'unveraendert', label: 'Status unverändert lassen', erklaerung: 'Nur den Verweis anhängen — ich bewerte die Antwort später.' },
    { wert: 'teilbeantwortet', label: 'Teilweise beantwortet', erklaerung: 'Das Dokument beantwortet die Frage nur zum Teil.' },
    { wert: 'beantwortet', label: 'Beantwortet', erklaerung: 'Das Dokument beantwortet die Frage; das Datum wird mitgeschrieben.' },
];

/**
 * Bestaetigung, bevor ein Dokument der Ablage an eine Antwort gehaengt wird.
 * Den Status waehlt der Mensch — Vorgabe ist „unveraendert": eine Datei mit
 * dem Fragecode im Namen kann auch eine Rueckfrage sein. Per Portal wie
 * `HandbuchImportDialog`.
 */
const AntwortVerknuepfenDialog: React.FC<{
    vorschlag: AntwortVorschlag;
    frage: Planungsfrage;
    onBestaetigen: (wahl: AntwortStatusWahl) => void;
    onAbbrechen: () => void;
}> = ({ vorschlag, frage, onBestaetigen, onAbbrechen }) => {
    const [wahl, setWahl] = useState<AntwortStatusWahl>('unveraendert');
    const verweis = dokumentVerweis(vorschlag.dokument);

    return createPortal(
        <div className="fixed inset-0 z-[80] bg-slate-900/50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[92vh] flex flex-col">
                <div className="flex items-start gap-3 px-5 py-4 border-b border-slate-200">
                    <Link2 className="w-5 h-5 text-indigo-600 shrink-0 mt-0.5" />
                    <div className="min-w-0 flex-1">
                        <h3 className="font-bold text-slate-800">Dokument als Antwort verknüpfen</h3>
                        <p className="text-xs text-slate-500 leading-snug">{vorschlag.grund}.</p>
                    </div>
                    <button type="button" onClick={onAbbrechen} className="p-1 text-slate-400 hover:text-slate-600" aria-label="Schließen">
                        <X className="w-4 h-4" />
                    </button>
                </div>
                <div className="px-5 py-4 space-y-4 overflow-y-auto">
                    <div>
                        <div className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-0.5">Frage</div>
                        <div className="text-sm text-slate-800 leading-snug">
                            <span className="font-mono text-[11px] font-bold text-slate-500 mr-2">{frage.code}</span>
                            {frage.frage}
                        </div>
                    </div>
                    <div>
                        <div className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-0.5">Antwort danach</div>
                        <div className="text-xs whitespace-pre-wrap break-words rounded-lg border px-2 py-1.5 max-h-40 overflow-y-auto bg-slate-50 border-slate-200 text-slate-600">
                            {frage.antwort ? <>{frage.antwort.replace(/\s+$/, '')}{'\n'}</> : null}
                            <span className="text-indigo-700 font-semibold">{verweis}</span>
                        </div>
                        <div className="text-[11px] text-slate-400 mt-1">
                            Die bisherige Antwort bleibt wortgetreu stehen; der Verweis kommt auf eine eigene Zeile. Als Quelle
                            werden das Dokument als Beleg und der Weg „Unterlage“ vermerkt, soweit dort noch nichts steht.
                        </div>
                    </div>
                    <fieldset>
                        <legend className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1">Status der Frage</legend>
                        <div className="space-y-1.5">
                            {STATUS_WAHL.map(w => (
                                <label key={w.wert} className={`flex items-start gap-2 rounded-lg border px-3 py-2 cursor-pointer ${
                                    wahl === w.wert ? 'border-indigo-300 bg-indigo-50/60' : 'border-slate-200 hover:bg-slate-50'
                                }`}>
                                    <input
                                        type="radio"
                                        name="antwort-status"
                                        checked={wahl === w.wert}
                                        onChange={() => setWahl(w.wert)}
                                        className="mt-0.5"
                                    />
                                    <span className="min-w-0">
                                        <span className="block text-sm font-bold text-slate-800">{w.label}</span>
                                        <span className="block text-[11px] text-slate-500">{w.erklaerung}</span>
                                    </span>
                                </label>
                            ))}
                        </div>
                    </fieldset>
                </div>
                <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-200">
                    <button type="button" onClick={onAbbrechen} className="px-3 py-1.5 text-xs font-bold text-slate-600 hover:text-slate-800">
                        Abbrechen
                    </button>
                    <button
                        type="button"
                        onClick={() => onBestaetigen(wahl)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700"
                    >
                        <Link2 className="w-3.5 h-3.5" /> Verknüpfen
                    </button>
                </div>
            </div>
        </div>,
        document.body,
    );
};

export const FragenListe: React.FC<Props> = ({
    fragen, heuteIso, isReadOnly, auswahl, onAuswahl, onAendern, onLoeschen, speichertId, empfaengerQuellen,
    ohneEmpfaenger,
}) => {
    const [filter, setFilter] = useState<FragenFilter>({ status: 'unerledigt' });
    const [zugeklappt, setZugeklappt] = useState<Set<KatalogKapitel>>(new Set());
    /** Ruecklauf: weggeklickte Vorschlaege dieser Sitzung (`dokumentId|frageId`). */
    const [ausgeblendet, setAusgeblendet] = useState<Set<string>>(new Set());
    /** Ruecklauf: der Vorschlag, der gerade bestaetigt wird. */
    const [verknuepfen, setVerknuepfen] = useState<AntwortVorschlag | null>(null);

    const gefiltert = useMemo(
        () => filterFragen(fragen, filter, heuteIso),
        [fragen, filter, heuteIso],
    );
    const gruppen = useMemo(() => gruppiereNachKapitel(gefiltert), [gefiltert]);

    /** Die Adressaten, die in den Fragen vorkommen — Filter und Zuordnung. */
    const vorhandeneAdressaten = useMemo(() => adressatenIn(fragen), [fragen]);

    // Alle Fragen der Liste gehoeren zu EINER Planungsphase; ohne Fragen gibt
    // es keine Adressaten und damit keinen Abschnitt — die Id kann deshalb
    // aus den Fragen kommen, statt als weitere Prop durchgereicht zu werden.
    const planungId = fragen.length ? fragen[0].planung_id : null;
    const empfaenger = useEmpfaengerZuordnung(planungId, isReadOnly);

    // Ruecklauf (Plan 4.3): Dokumente der Projektablage, deren Titel,
    // Dateiname oder Beschreibung einen Fragecode nennt. Die Projekt-Id
    // kommt wie die Phasen-Id aus den Fragen. Nur Vorschlaege — den Verweis
    // und den Status bestaetigt der Mensch im Dialog.
    const projectId = fragen.length ? fragen[0].project_id : null;
    const ablage = useDokumente(projectId);
    const antwortVorschlaege = useMemo(
        () => (ablage.laden || ablage.fehler ? [] : rueckflussVorschlaege(ablage.dokumente, [], fragen).antworten)
            .filter(v => !ausgeblendet.has(`${v.dokument.id}|${v.frageId}`)),
        [ablage.laden, ablage.fehler, ablage.dokumente, fragen, ausgeblendet],
    );
    /** Fehler beim Oeffnen eines Ruecklauf-Dokuments — steht im Kasten, nie still. */
    const [rueckflussFehler, setRueckflussFehler] = useState<string | null>(null);
    const frageZu = (id: string): Planungsfrage | undefined => fragen.find(f => f.id === id);
    /** Der Beleg einer Antwort (`antwort_beleg_id`), soweit die Ablage ihn zeigt. */
    const belegZu = (f: Planungsfrage): ProjektDokument | null =>
        (f.antwort_beleg_id && ablage.dokumente.find(d => d.id === f.antwort_beleg_id)) || null;

    const dokumentOeffnen = (id: string) => {
        const dok: ProjektDokument | undefined = ablage.dokumente.find(d => d.id === id);
        if (!dok) { setRueckflussFehler('Das Dokument ist in der Ablage nicht mehr verfügbar.'); return; }
        setRueckflussFehler(null);
        dokumentAnsehen(dok).catch(err => setRueckflussFehler(`Das Dokument konnte nicht geöffnet werden: ${describeError(err)}`));
    };

    const antwortVerknuepfen = (v: AntwortVorschlag, wahl: AntwortStatusWahl) => {
        const frage = frageZu(v.frageId);
        setVerknuepfen(null);
        if (!frage || isReadOnly) return;
        onAendern(frage.id, antwortMitDokument(frage, v.dokument, wahl));
    };

    const vorschlagAusblenden = (v: AntwortVorschlag) => {
        setAusgeblendet(alt => new Set(alt).add(`${v.dokument.id}|${v.frageId}`));
    };

    const alleGewaehlt = gefiltert.length > 0 && gefiltert.every(f => auswahl.has(f.id));
    const umschaltenAlle = () => {
        const neu = new Set(auswahl);
        if (alleGewaehlt) gefiltert.forEach(f => neu.delete(f.id));
        else gefiltert.forEach(f => neu.add(f.id));
        onAuswahl(neu);
    };

    const kapitelUmschalten = (k: KatalogKapitel) => {
        setZugeklappt(alt => {
            const neu = new Set(alt);
            if (neu.has(k)) neu.delete(k); else neu.add(k);
            return neu;
        });
    };

    return (
        <div className="space-y-4">

            {/* ─── Empfaenger je Adressat (Stufe 2.3) ────────────────── */}
            {!ohneEmpfaenger && (
                <EmpfaengerZuordnung
                    adressaten={vorhandeneAdressaten}
                    zuordnung={empfaenger.zuordnung}
                    quellen={empfaengerQuellen}
                    isReadOnly={isReadOnly}
                    laden={empfaenger.laden}
                    hinweis={empfaenger.hinweis}
                    meldung={empfaenger.meldung}
                    stand={empfaenger.stand}
                    fehler={empfaenger.fehler}
                    schreibbar={empfaenger.schreibbar}
                    onSetzen={(a, e) => { empfaenger.setze(a, e); }}
                    onErneutSpeichern={() => { empfaenger.erneutSpeichern(); }}
                    onMeldungSchliessen={empfaenger.schliesseMeldung}
                />
            )}

            {/* ─── Ruecklauf aus der Ablage (Plan 4.3) ──────────────── */}
            {antwortVorschlaege.length > 0 && (
                <div className="bg-white rounded-2xl border border-indigo-200 overflow-hidden">
                    <div className="flex items-center gap-3 px-4 py-3 bg-indigo-50/60 border-b border-indigo-100">
                        <Inbox className="w-4 h-4 text-indigo-600 shrink-0" />
                        <span className="font-bold text-slate-800 text-sm flex-1 min-w-0">Rücklauf aus der Projektablage</span>
                        <span className="text-xs text-slate-400 shrink-0">{antwortVorschlaege.length} Vorschl{antwortVorschlaege.length === 1 ? 'ag' : 'äge'}</span>
                    </div>
                    <p className="px-4 py-2 text-[11px] text-slate-500 italic bg-slate-50/50 border-b border-slate-100">
                        Hochgeladene Dokumente, die einen Fragecode nennen. Verknüpfen hängt „Dokument: Titel (Ablage)" an die
                        Antwort — den Status der Frage wählen Sie dabei selbst, nichts wird still als geklärt geführt.
                    </p>
                    {rueckflussFehler && (
                        <div className="px-4 py-2 text-xs text-rose-700 bg-rose-50 border-b border-rose-100 flex items-center gap-2">
                            <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                            <span className="flex-1 min-w-0">{rueckflussFehler}</span>
                            <button type="button" onClick={() => setRueckflussFehler(null)} className="font-bold underline">Schließen</button>
                        </div>
                    )}
                    {antwortVorschlaege.map(v => {
                        const frage = frageZu(v.frageId);
                        return (
                            <div key={`${v.dokument.id}|${v.frageId}`} className="px-4 py-3 border-b border-slate-100 last:border-0 flex items-start gap-3 flex-wrap">
                                <Link2 className="w-4 h-4 text-indigo-500 shrink-0 mt-0.5" />
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm text-slate-800">
                                        <button
                                            type="button"
                                            onClick={() => dokumentOeffnen(v.dokument.id)}
                                            className="font-bold text-blue-600 hover:text-blue-700 inline-flex items-center gap-1 max-w-full"
                                            title="Dokument ansehen"
                                        >
                                            <span className="truncate">{v.dokument.titel || v.dokument.dateiname}</span>
                                            <ExternalLink className="w-3 h-3 shrink-0" />
                                        </button>
                                        {' '}→ Antwort auf <span className="font-mono font-bold">{v.code}</span>
                                    </div>
                                    {frage && <div className="text-xs text-slate-500 mt-0.5 line-clamp-2">{frage.frage}</div>}
                                    <div className="text-[11px] text-slate-400 mt-0.5">{v.grund}{frage ? ` · Stand: ${STATUS_LABEL[frage.status]}` : ''}</div>
                                </div>
                                <div className="shrink-0 flex items-center gap-2">
                                    {!isReadOnly && frage && (
                                        <button
                                            type="button"
                                            onClick={() => setVerknuepfen(v)}
                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100"
                                        >
                                            <Link2 className="w-3.5 h-3.5" /> Dokument als Antwort verknüpfen …
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => vorschlagAusblenden(v)}
                                        className="text-xs font-bold text-slate-500 hover:text-slate-700"
                                        title="Vorschlag für diese Sitzung ausblenden"
                                    >
                                        Nicht verknüpfen
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* ─── Filterleiste ──────────────────────────────────────── */}
            <div className="bg-white rounded-2xl border border-slate-200 p-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                    <input
                        type="search"
                        value={filter.suche || ''}
                        onChange={e => setFilter(f => ({ ...f, suche: e.target.value }))}
                        placeholder="Suchen in Code, Frage, Antwort …"
                        className="lg:col-span-2 px-3 py-2 border border-slate-200 rounded-lg text-sm"
                    />
                    <select
                        value={filter.kapitel || 'alle'}
                        onChange={e => setFilter(f => ({ ...f, kapitel: e.target.value as any }))}
                        className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
                    >
                        <option value="alle">Alle Kapitel</option>
                        {KAPITEL.map(k => <option key={k.key} value={k.key}>{k.key} — {k.titel}</option>)}
                    </select>
                    <select
                        value={filter.status || 'alle'}
                        onChange={e => setFilter(f => ({ ...f, status: e.target.value as any }))}
                        className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
                    >
                        <option value="unerledigt">Nur unerledigte</option>
                        <option value="alle">Alle Stände</option>
                        {STATUS_WERTE.map(s => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
                    </select>
                    <select
                        value={filter.adressat || 'alle'}
                        onChange={e => setFilter(f => ({ ...f, adressat: e.target.value as any }))}
                        className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
                    >
                        <option value="alle">Alle Adressaten</option>
                        {vorhandeneAdressaten.map(a => (
                            <option key={a} value={a}>{a} — {ADRESSAT_LABEL[a]}</option>
                        ))}
                    </select>
                </div>
                <div className="flex items-center gap-4 mt-3 pt-3 border-t border-slate-100 flex-wrap">
                    <label className="flex items-center gap-2 text-xs text-slate-600">
                        <input
                            type="checkbox"
                            checked={filter.kritikalitaet === 'A'}
                            onChange={e => setFilter(f => ({ ...f, kritikalitaet: e.target.checked ? 'A' : 'alle' }))}
                            className="w-4 h-4 rounded border-slate-300"
                        />
                        Nur haftungsrelevante (A)
                    </label>
                    <label className="flex items-center gap-2 text-xs text-slate-600">
                        <input
                            type="checkbox"
                            checked={!!filter.nurUeberfaellig}
                            onChange={e => setFilter(f => ({ ...f, nurUeberfaellig: e.target.checked }))}
                            className="w-4 h-4 rounded border-slate-300"
                        />
                        Nur überfällige
                    </label>
                    <button
                        type="button"
                        onClick={umschaltenAlle}
                        disabled={!gefiltert.length}
                        className="text-xs font-bold text-blue-600 hover:text-blue-700 disabled:text-slate-300"
                    >
                        {alleGewaehlt ? 'Auswahl aufheben' : `Alle ${gefiltert.length} auswählen`}
                    </button>
                    <span className="text-xs text-slate-400 ml-auto">
                        {gefiltert.length} von {fragen.length} Fragen
                        {auswahl.size > 0 && <> · {auswahl.size} ausgewählt</>}
                    </span>
                </div>
            </div>

            {/* ─── Liste ─────────────────────────────────────────────── */}
            {gruppen.length === 0 && (
                <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center">
                    <p className="text-sm text-slate-500">Keine Frage passt zu diesem Filter.</p>
                </div>
            )}

            {gruppen.map(g => {
                const info = KAPITEL.find(k => k.key === g.kapitel);
                const zu = zugeklappt.has(g.kapitel);
                const offeneKritische = g.fragen.filter(f =>
                    f.kritikalitaet === 'A' && f.status !== 'beantwortet' && f.status !== 'gegenstandslos').length;
                return (
                    <div key={g.kapitel} className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                        <button
                            type="button"
                            onClick={() => kapitelUmschalten(g.kapitel)}
                            className="w-full flex items-center gap-3 px-4 py-3 bg-slate-50 border-b border-slate-200 text-left hover:bg-slate-100 transition-colors"
                        >
                            {zu ? <ChevronRight className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                            <span className="font-black text-slate-400 text-sm w-4">{g.kapitel}</span>
                            <span className="font-bold text-slate-800 text-sm flex-1 min-w-0">{KAPITEL_TITEL(g.kapitel)}</span>
                            {offeneKritische > 0 && (
                                <span className="inline-flex items-center gap-1 text-[11px] font-black text-rose-600 shrink-0">
                                    <AlertTriangle className="w-3.5 h-3.5" /> {offeneKritische}
                                </span>
                            )}
                            <span className="text-xs text-slate-400 shrink-0">{g.fragen.length}</span>
                        </button>

                        {!zu && (
                            <>
                                {info?.hinweis && (
                                    <p className="px-4 py-2 text-[11px] text-slate-500 italic bg-slate-50/50 border-b border-slate-100">
                                        {info.hinweis}
                                    </p>
                                )}
                                {g.fragen.map(f => (
                                    <FrageZeile
                                        key={f.id}
                                        frage={f}
                                        heuteIso={heuteIso}
                                        isReadOnly={isReadOnly}
                                        gewaehlt={auswahl.has(f.id)}
                                        empfaenger={empfaenger.zuordnung}
                                        empfaengerQuellen={empfaengerQuellen}
                                        beleg={belegZu(f)}
                                        ablageGelesen={!ablage.laden && !ablage.fehler}
                                        onWahl={an => {
                                            const neu = new Set(auswahl);
                                            if (an) neu.add(f.id); else neu.delete(f.id);
                                            onAuswahl(neu);
                                        }}
                                        onAendern={u => onAendern(f.id, u)}
                                        onLoeschen={() => onLoeschen(f)}
                                        speichert={speichertId === f.id}
                                    />
                                ))}
                            </>
                        )}
                    </div>
                );
            })}

            {verknuepfen && frageZu(verknuepfen.frageId) && (
                <AntwortVerknuepfenDialog
                    key={`${verknuepfen.dokument.id}|${verknuepfen.frageId}`}
                    vorschlag={verknuepfen}
                    frage={frageZu(verknuepfen.frageId)!}
                    onBestaetigen={wahl => antwortVerknuepfen(verknuepfen, wahl)}
                    onAbbrechen={() => setVerknuepfen(null)}
                />
            )}
        </div>
    );
};

/** Kleine Kopfleiste mit den Sammelaktionen. Getrennt von der Liste, damit
 *  sie im Modul oben stehen kann, wo sie sichtbar bleibt. */
export const AuswahlAktionen: React.FC<{
    anzahl: number;
    isReadOnly: boolean;
    onVersenden: () => void;
    onBedenken: () => void;
    onLeeren: () => void;
}> = ({ anzahl, isReadOnly, onVersenden, onBedenken, onLeeren }) => {
    if (!anzahl) return null;
    return (
        <div className="sticky top-0 z-20 bg-blue-600 text-white rounded-xl px-4 py-2.5 flex items-center gap-3 flex-wrap shadow-lg">
            <span className="text-sm font-bold">{anzahl} Frage{anzahl === 1 ? '' : 'n'} ausgewählt</span>
            <div className="flex items-center gap-2 ml-auto flex-wrap">
                <button
                    type="button"
                    onClick={onVersenden}
                    disabled={isReadOnly}
                    className="inline-flex items-center gap-1.5 bg-white/15 hover:bg-white/25 disabled:opacity-50 px-3 py-1.5 rounded-lg text-xs font-bold transition-colors"
                >
                    <Send className="w-3.5 h-3.5" /> Als gestellt markieren
                </button>
                <button
                    type="button"
                    onClick={onBedenken}
                    disabled={isReadOnly}
                    className="inline-flex items-center gap-1.5 bg-white/15 hover:bg-white/25 disabled:opacity-50 px-3 py-1.5 rounded-lg text-xs font-bold transition-colors"
                >
                    <AlertTriangle className="w-3.5 h-3.5" /> Bedenken anmelden
                </button>
                <button type="button" onClick={onLeeren} className="text-xs font-bold underline opacity-80 hover:opacity-100">
                    Auswahl aufheben
                </button>
            </div>
        </div>
    );
};
